import type { ChildId, ChildWithProfile, ContentItem } from '@kidshub/contracts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/api-client.js';
import { toErrorKey } from '../lib/error-code.js';
import { Icon } from '../ui/kit.js';

/**
 * Who can see this, by name.
 *
 * This is the product's centre of gravity and the screen has to carry it as
 * one. An item in the library belongs to nobody: adding is not approving, and a
 * piece of content reaches a child only when a parent opens it to that child by
 * name. Default deny (`SPEC.md` §2.1) is not a setting with a sensible default —
 * it is the state everything starts in.
 *
 * Three deliberate choices keep it from reading as a settings toggle:
 *
 * - The row asks a question about people rather than labelling a feature. A
 *   parent is answering "who sees this", not enabling "sharing". The question
 *   names the group for a screen reader; sighted parents read it off the chips.
 * - Every child in the family is always listed, granted or not, and a closed
 *   chip says "closed" in a word as well as in its dashed outline.
 * - An item nobody has been given says so in a sentence. An empty row would
 *   read as "nothing configured yet"; the truth is stronger than that, and it
 *   is the truth the parent is responsible for.
 */

interface ApprovalChipsProps {
  item: ContentItem;
  familyChildren: ChildWithProfile[];
  /** Handed the item the server returned, so the card redraws from the truth. */
  onChange: (item: ContentItem) => void;
}

export const ApprovalChips = ({ item, familyChildren, onChange }: ApprovalChipsProps) => {
  const { t } = useTranslation();
  const [pendingChildId, setPendingChildId] = useState<ChildId | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const approvedFor = new Set<string>(item.approvedForChildIds);

  const toggle = async (childId: ChildId, isApproved: boolean) => {
    setErrorKey(null);
    setPendingChildId(childId);

    try {
      onChange(
        await apiClient.catalog.setApproval({ contentItemId: item.id, childId, isApproved }),
      );
    } catch (error) {
      setErrorKey(toErrorKey(error));
    } finally {
      setPendingChildId(null);
    }
  };

  return (
    <fieldset className="lib-approval">
      <legend className="sr">{t('library.whoSeesThis')}</legend>

      <div className="lib-appr-row">
        {familyChildren.map((entry) => {
          const isApproved = approvedFor.has(entry.child.id);

          return (
            <button
              key={entry.child.id}
              type="button"
              className="appr"
              aria-pressed={isApproved}
              disabled={pendingChildId !== null}
              onClick={() => void toggle(entry.child.id, !isApproved)}
              title={t(isApproved ? 'library.withdrawFrom' : 'library.openTo', {
                name: entry.child.name,
              })}
            >
              {isApproved ? <Icon name="check" small /> : null}
              {entry.child.name}
              {/* The name alone is the button's name, and aria-pressed its state;
                  the word is for eyes that cannot tell a dashed edge from a solid one. */}
              {isApproved ? null : (
                <span className="lib-appr-off" aria-hidden="true">
                  {t('library.approval.closed')}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {approvedFor.size === 0 ? <p className="lib-note">{t('library.nobodySeesThis')}</p> : null}

      {errorKey === null ? null : (
        <p className="help err" role="alert">
          {t(errorKey)}
        </p>
      )}
    </fieldset>
  );
};
